import React, { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { Breadcrumb, Label, ListGroup, Tooltip } from "flowbite-react";
import {
  HiBell,
  HiCheck,
  HiClipboard,
  HiCog,
  HiDocumentText,
  HiExclamation,
  HiLink,
  HiMail,
  HiOutlineChevronRight,
  HiOutlineDotsVertical,
  HiPencil,
  HiPlusCircle,
  HiRefresh,
} from "react-icons/hi";
import { FiEdit3 } from "react-icons/fi";
import { FiGitMerge } from "react-icons/fi";
import { BiHistory } from "react-icons/bi";
import { FaGlasses } from "react-icons/fa";
import CustomButton from "../../../components/common/CustomButton";
import CustomTooltip from "../../../components/common/CustomTooltip";
import TruncatedText from "../../../components/common/TruncatedText";
import { ENTITY } from "../../../redux/slices/timeline";
import { useProjectService, useRoadmapService } from "../../../services";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import { getTimeZoneDate } from "../../../helpers/data-format";
import { useLocation } from "react-router-dom";
import { PreviousBreadCrumb } from "../../../misc/patches/PreviousBreadCrumb";
import SimFlag from "../../../features/SimFlag/SimFlag";
import { HorizontalScrollView } from "../../../shared/molecules/HorizontalScrollView/HorizontalScrollView";

const horizontalBounceKeyframes = `
  @keyframes horizontal-bounce {
    0%, 20%, 53%, 80%, 100% {
      transform: translate3d(0, 0, 0);
    }
    40%, 43% {
      transform: translate3d(4px, 0, 0);
    }
    70% {
      transform: translate3d(2px, 0, 0);
    }
    90% {
      transform: translate3d(1px, 0, 0);
    }
  }
`;
const styleElement = document.createElement("style");
styleElement.textContent = horizontalBounceKeyframes;
if (!document.head.querySelector("style[data-horizontal-bounce-animation]")) {
  styleElement.setAttribute("data-horizontal-bounce-animation", "true");
  document.head.appendChild(styleElement);
}

export type BreadcrumbItem = {
  label: string;
  href: string;
  active?: boolean;
};

type BreadcrumbHeaderProps = {
  breadcrumbs: BreadcrumbItem[];
  children?: ReactNode;
  showSimToggle?: boolean;
  simFlag?: boolean;
  onToggleSim?: () => void;
  onRefresh?: () => void;
  showRefreshButton?: boolean;
  refreshButtonText?: string;
  setShowRoadmapModal: any;
  onBreadcrumbClick?: (index: number, item: BreadcrumbItem) => void;
  isLoading?: boolean;
  onCreateTask?: () => void;
  onEditProject?: () => void;
  onMerge?: () => void;
  onReset?: () => void;
  onVersionHistory?: () => void;
  onProjectHistory?: () => void;
  onShare?: () => void;
  onSyncTickets?: () => void;
  onNotifications?: () => void;
  selectedProject?: any;
  sim_flag?: boolean;
  org_role?: string;
  onResyncRoadmap?: () => void;
  onResyncProject?: () => void;
  integration_data?: any;
  projectIntegrationData?: any;
  syncStatus?: string;
  lastSyncedAt?: string;
  projectSyncStatus?: string;
  projectLastSyncedAt?: string;
  simulationChanges?: {
    hasChanges: boolean;
  };
  readOnly?: boolean;
  onEditTemplate?: () => void;
  templateData?: any;
  onLinkItems?: () => void;
};

// Skeleton component with global colors
const Skeleton = ({
  width,
  height = "h-4",
  className = "",
}: {
  width: string;
  height?: string;
  className?: string;
}) => (
  <div
    className={`${height} ${width} bg-light-surfaceSecondary dark:bg-gray-700 rounded animate-pulse ${className}`}
  ></div>
);

const BreadcrumbHeader: React.FC<BreadcrumbHeaderProps> = ({
  breadcrumbs,
  children,
  showSimToggle = false,
  simFlag = false,
  onToggleSim,
  onRefresh,
  showRefreshButton = false,
  refreshButtonText = "Refresh data",
  setShowRoadmapModal,
  onBreadcrumbClick,
  isLoading = false,
  onCreateTask,
  onEditProject,
  onMerge,
  onReset,
  onVersionHistory,
  onProjectHistory,
  onShare,
  onSyncTickets,
  onNotifications,
  org_role,
  onResyncRoadmap,
  onResyncProject,
  integration_data,
  projectIntegrationData,
  syncStatus = "idle",
  lastSyncedAt,
  projectSyncStatus = "idle",
  projectLastSyncedAt,
  simulationChanges,
  readOnly = false,
  onEditTemplate,
  templateData,
  onLinkItems,
}) => {
  const [showListGroup, setShowListGroup] = useState(false);
  const [copiedLinkType, setCopiedLinkType] = useState<string | null>(null);
  const [maxTextWidth, setMaxTextWidth] = useState(200);
  const [shareEnabled, setShareEnabled] = useState(false);
  const [shareToggleLoading, setShareToggleLoading] = useState(false);
  const [notificationTogglesLoading, setNotificationTogglesLoading] = useState<{
    [key: string]: boolean;
  }>({});
  const breadcrumbContainerRef = useRef<HTMLDivElement>(null);

  // Update max text width based on available space
  useEffect(() => {
    const updateMaxWidth = () => {
      if (breadcrumbContainerRef.current) {
        const containerWidth = breadcrumbContainerRef.current.offsetWidth;
        const itemCount = breadcrumbs.length;

        // Calculate total text width needed for all breadcrumbs
        let totalTextWidth = 0;
        breadcrumbs.forEach((item) => {
          if (item.label) {
            // Create temporary span to measure text width
            const tempSpan = document.createElement("span");
            tempSpan.style.visibility = "hidden";
            tempSpan.style.position = "absolute";
            tempSpan.style.fontSize = "14px"; // Default font size
            tempSpan.style.fontWeight = "500"; // font-medium
            tempSpan.textContent = item.label;
            document.body.appendChild(tempSpan);
            totalTextWidth += tempSpan.offsetWidth;
            document.body.removeChild(tempSpan);
          }
        });

        // Reserve space for arrows and spacing (24px per item)
        const availableTextWidth = containerWidth - itemCount * 24;

        // If total text fits, don't truncate
        if (totalTextWidth <= availableTextWidth) {
          setMaxTextWidth(9999); // Very large number to prevent truncation
        } else {
          // Calculate truncated width per item
          const newMaxWidth = Math.max(80, availableTextWidth / itemCount);
          setMaxTextWidth(newMaxWidth);
        }
      }
    };

    // Initial calculation
    updateMaxWidth();

    // Add resize listener
    window.addEventListener("resize", updateMaxWidth);

    // Cleanup
    return () => {
      window.removeEventListener("resize", updateMaxWidth);
    };
  }, [breadcrumbs.length]);

  const { downloadRoadmapReport } = useRoadmapService();
  const {
    updateProjectNotificationSetting,
    fetchProjectShareTokens,
    updateProject,
  } = useProjectService();

  const {
    singleRoadmapRes = [],
    entity_type,
    share_link: shareLinkFromStore = false,
  } = useSelector((state: any) => state.roadmap);

  const { currentEntity, currentEntityId, simulation } = useSelector(
    (state: any) => state.timeline,
  );
  const { template_id } = useSelector((state: any) => state.templates);
  const location = useLocation();
  const isTemplatesListPage =
    location.pathname === "/templates" || location.pathname === "/templates/";
  const isTemplate = !!template_id && !isTemplatesListPage;

  const hasRoadmapChildren = React.useMemo(() => {
    return (
      Array.isArray(singleRoadmapRes?.projects) &&
      (singleRoadmapRes.projects as any[])?.length > 0
    );
  }, [singleRoadmapRes?.projects]);

  const isResyncDisabled = syncStatus === "in_progress";

  const {
    notificationSettingLoading,
    notificationSettingData: nsd,
    projectShareData,
  } = useSelector((state: any) => state.projects);

  useEffect(() => {
    setShareEnabled(!!shareLinkFromStore);
  }, [shareLinkFromStore]);

  useEffect(() => {
    if (
      !readOnly &&
      currentEntity === ENTITY.PROJECT &&
      currentEntityId &&
      shareLinkFromStore &&
      (!projectShareData ||
        !projectShareData.live ||
        !projectShareData.simulation)
    ) {
      fetchProjectShareTokens(currentEntityId);
    }
  }, [currentEntity, currentEntityId, shareLinkFromStore, readOnly]);

  const handleShareToggle = async () => {
    if (readOnly) return;
    if (currentEntity === ENTITY.PROJECT && currentEntityId) {
      const newShareEnabled = !shareEnabled;
      setShareToggleLoading(true);

      try {
        // Update the UI state first for immediate feedback
        setShareEnabled(newShareEnabled);

        // Update the project settings
        await updateProject(currentEntityId, {
          share_link: newShareEnabled,
          isFetchAllProjects: false,
        });

        if (newShareEnabled) {
          // Fetch share tokens when enabling
          await fetchProjectShareTokens(currentEntityId);
        }
      } catch (error) {
        // Revert state on error
        setShareEnabled(!newShareEnabled);
        toast.error("Failed to update share settings", {
          style: {
            background: "var(--toast-bg, #ffffff)",
            color: "var(--toast-text, #000000)",
            border: "1px solid var(--toast-border, #e5e7eb)",
          },
          className:
            "dark:!bg-gray-800 dark:!text-white text-sm dark:!border-gray-600",
        });
      } finally {
        setShareToggleLoading(false);
      }
    }
  };

  const copyShareableLink = async (
    url: string,
    linkType: "live" | "simulation",
  ) => {
    try {
      if (shareEnabled && url) {
        await navigator.clipboard.writeText(url);
        setCopiedLinkType(linkType);
        toast.success("Shareable link copied!", {
          style: {
            background: "var(--toast-bg, #ffffff)",
            color: "var(--toast-text, #000000)",
            border: "1px solid var(--toast-border, #e5e7eb)",
          },
          className:
            "dark:!bg-gray-800 dark:!text-white text-sm dark:!border-gray-600",
        });

        setTimeout(() => {
          setCopiedLinkType(null);
        }, 2000);
      } else {
        toast.error("Please enable shareable link first", {
          style: {
            background: "var(--toast-bg, #ffffff)",
            color: "var(--toast-text, #000000)",
            border: "1px solid var(--toast-border, #e5e7eb)",
          },
          className:
            "dark:!bg-gray-800 dark:!text-white text-sm dark:!border-gray-600",
        });
      }
    } catch (error) {
      toast.error("Failed to copy link", {
        style: {
          background: "var(--toast-bg, #ffffff)",
          color: "var(--toast-text, #000000)",
          border: "1px solid var(--toast-border, #e5e7eb)",
        },
        className:
          "dark:!bg-gray-800 dark:!text-white text-sm dark:!border-gray-600",
      });
    }
  };

  const handleNotificationToggle = async (settingKey: string) => {
    if (currentEntity === ENTITY.PROJECT && currentEntityId && nsd) {
      const currentValue = nsd[settingKey];
      const newValue = !currentValue;
      const payload = { ...nsd, [settingKey]: newValue };

      // Set loading state for this specific toggle
      setNotificationTogglesLoading((prev) => ({
        ...prev,
        [settingKey]: true,
      }));

      try {
        await updateProjectNotificationSetting(currentEntityId, payload);
      } catch (error) {
        toast.error("Failed to update notification settings", {
          style: {
            background: "var(--toast-bg, #ffffff)",
            color: "var(--toast-text, #000000)",
            border: "1px solid var(--toast-border, #e5e7eb)",
          },
          className:
            "dark:!bg-gray-800 dark:!text-white text-sm dark:!border-gray-600",
        });
      } finally {
        // Clear loading state for this specific toggle
        setNotificationTogglesLoading((prev) => ({
          ...prev,
          [settingKey]: false,
        }));
      }
    }
  };

  const handleListGroupClick = (type: string) => {
    if (type === "Edit") {
      setShowRoadmapModal(true);
    }
    if (type === "download") {
      if (currentEntity === ENTITY.ROADMAP && currentEntityId) {
        downloadRoadmapReport(
          currentEntityId,
          simulation,
          singleRoadmapRes.name,
        );
      }
    }
    if (type === "resync") {
      if (onResyncRoadmap) {
        onResyncRoadmap();
      }
    }
    // Project-specific actions
    if (type === "editProject" && onEditProject) {
      onEditProject();
    }
    if (type === "merge" && onMerge) {
      onMerge();
    }
    if (type === "reset" && onReset) {
      onReset();
    }
    if (type === "versionHistory" && onVersionHistory) {
      onVersionHistory();
    }
    if (type === "projectHistory" && onProjectHistory) {
      onProjectHistory();
    }
    if (type === "share" && onShare) {
      onShare();
    }
    if (type === "syncTickets" && onSyncTickets) {
      onSyncTickets();
    }
    if (type === "notifications" && onNotifications) {
      onNotifications();
    }
    if (type === "resyncProject") {
      if (onResyncProject) {
        onResyncProject();
      }
    }
    setShowListGroup(false);
  };

  const renderRoadmapMenuItems = () => (
    <>
      <ListGroup.Item
        onClick={() => handleListGroupClick("Edit")}
        className="hover:bg-light-interactive-hover dark:hover:bg-slate-700 border-none cursor-pointer transition-colors duration-200"
        theme={{
          link: {
            base: "flex justify-between items-center focus:outline-none w-full py-2 px-4 border-none text-light-text-primary dark:text-white",
          },
        }}
      >
        <span>Edit</span> <HiPencil />
      </ListGroup.Item>
      <ListGroup.Item
        onClick={() => handleListGroupClick("download")}
        className="hover:bg-light-interactive-hover dark:hover:bg-slate-700 border-none cursor-pointer transition-colors duration-200"
        theme={{
          link: {
            base: "flex justify-between items-center focus:outline-none w-full py-2 px-4 border-none text-light-text-primary dark:text-white",
          },
        }}
      >
        <span>Download Map</span> <HiDocumentText />
      </ListGroup.Item>
      {integration_data?.integration_type === "azure" && (
        <ListGroup.Item
          onClick={() => {
            if (isResyncDisabled) return;
            handleListGroupClick("resync");
          }}
          className={`border-none cursor-pointer transition-colors duration-200 ${
            isResyncDisabled
              ? "opacity-50 cursor-not-allowed bg-light-surfaceSecondary dark:bg-gray-700"
              : "hover:bg-light-interactive-hover dark:hover:bg-slate-700"
          }`}
          style={{
            cursor: isResyncDisabled ? "not-allowed" : "pointer",
          }}
          theme={{
            link: {
              base: "flex justify-between items-center focus:outline-none w-full py-3 px-4 border-none text-light-text-primary dark:text-white",
            },
          }}
        >
          <div className="flex flex-col items-start space-y-1.5 flex-1">
            <div className="flex items-center justify-between w-full">
              <span
                className={`font-medium ${isResyncDisabled ? "text-gray-500" : "text-light-text-primary dark:text-white"}`}
              >
                {syncStatus === "in_progress" ? "Syncing..." : "Resync"}
              </span>
              <HiRefresh
                className={`w-5 h-5 ${
                  syncStatus === "in_progress"
                    ? "animate-spin text-gray-500"
                    : "text-gray-600 dark:text-gray-400"
                }`}
              />
            </div>
            <span className="text-xs text-gray-500 dark:text-gray-400 text-left leading-relaxed">
              {lastSyncedAt
                ? `Last: ${getTimeZoneDate(lastSyncedAt, "lll")}`
                : "Last: Never"}
            </span>
          </div>
        </ListGroup.Item>
      )}
    </>
  );

  const renderTemplateMenuItems = () => (
    <>
      <ListGroup.Item
        onClick={() => {
          if (onEditTemplate) {
            onEditTemplate();
          }
          setShowListGroup(false);
        }}
        className="hover:bg-light-interactive-hover dark:hover:bg-slate-700 border-none cursor-pointer transition-colors duration-200"
        theme={{
          link: {
            base: "flex justify-between items-center focus:outline-none w-full py-2 px-4 border-none text-light-text-primary dark:text-white",
          },
        }}
      >
        <span>Edit Template </span> <FiEdit3 />
      </ListGroup.Item>
    </>
  );

  const renderProjectMenuItems = useMemo(() => (()=>
    {
      console.log("integrationdata", projectIntegrationData?.integration_type);
      return <>
        <ListGroup.Item
          onClick={() => handleListGroupClick("editProject")}
          className="hover:bg-light-interactive-hover dark:hover:bg-slate-700 border-none cursor-pointer transition-colors duration-200"
          theme={{
            link: {
              base: "flex justify-between items-center focus:outline-none w-full py-2 px-4 border-none text-light-text-primary dark:text-white",
            },
          }}
        >
          <span>Edit</span> <HiCog />
        </ListGroup.Item>

        <div className="relative group">
          <ListGroup.Item
            className="hover:bg-light-interactive-hover dark:hover:bg-slate-700 border-none cursor-pointer transition-colors duration-200"
            theme={{
              link: {
                base: "flex justify-between items-center focus:outline-none w-full py-2 px-4 border-none text-light-text-primary dark:text-white",
              },
            }}
          >
            <span>Share</span> <HiOutlineChevronRight />
          </ListGroup.Item>
          <div className="absolute right-full top-0 w-64 bg-light-surfacePrimary dark:bg-slate-800 rounded-md shadow-lg border border-light-border-default dark:border-gray-700 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-opacity duration-75 z-50">
            <div className="p-4">
              <div className="flex justify-start">
                <h4 className="text-sm font-semibold text-light-text-primary dark:text-white mb-4">
                  {`Share this ${entity_type}`}
                </h4>
              </div>

              <div className="space-y-4">
                {/* Enable Shareable Link Toggle */}
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <button
                      type="button"
                      onClick={handleShareToggle}
                      disabled={shareToggleLoading}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-gray-800 ${shareEnabled
                          ? "bg-blue-600 dark:bg-blue-500"
                          : "bg-gray-200 dark:bg-gray-600"} ${shareToggleLoading ? "opacity-75 cursor-not-allowed" : "cursor-pointer"}`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ease-in-out ${shareEnabled ? "translate-x-6" : "translate-x-1"}`}
                      >
                        {shareToggleLoading && (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="h-2.5 w-2.5 border border-gray-300 border-t-transparent rounded-full animate-spin"></div>
                          </div>
                        )}
                      </span>
                    </button>
                  </div>
                  <Label className="text-sm text-light-text-primary dark:text-white">
                    Shareable link
                  </Label>
                </div>

                {shareEnabled ? (
                  projectShareData &&
                    projectShareData.live &&
                    projectShareData.simulation ? (
                    <div className="space-y-3">
                      {/* Live URL */}
                      <div>
                        <Label className="text-xs text-light-text-secondary dark:text-gray-400 mb-1 block">
                          Live URL
                        </Label>
                        <div className="flex items-center gap-2 p-2 bg-light-surfaceSecondary dark:bg-slate-700 rounded border border-light-border-default dark:border-gray-600">
                          <HiLink className="text-light-text-secondary dark:text-gray-400 flex-shrink-0 text-sm" />
                          <span className="text-xs text-light-text-secondary dark:text-gray-300 truncate flex-1">
                            {`${window.location.host}/projects/${projectShareData.live}/sharelink`}
                          </span>
                          <button
                            onClick={() => copyShareableLink(
                              `${window.location.protocol}//${window.location.host}/projects/${projectShareData.live}/sharelink`,
                              "live"
                            )}
                            className="p-1 text-light-text-secondary dark:text-gray-300 hover:text-primary-light dark:hover:text-blue-400 hover:bg-light-interactive-hover dark:hover:bg-slate-600 rounded transition-colors duration-200 flex-shrink-0"
                          >
                            {copiedLinkType === "live" ? (
                              <HiCheck className="w-3 h-3 text-green-600 dark:text-green-400" />
                            ) : (
                              <HiClipboard className="w-3 h-3" />
                            )}
                          </button>
                        </div>
                      </div>

                      {/* Simulation URL */}
                      <div>
                        <Label className="text-xs text-light-text-secondary dark:text-gray-400 mb-1 block">
                          Simulation URL
                        </Label>
                        <div className="flex items-center gap-2 p-2 bg-light-surfaceSecondary dark:bg-slate-700 rounded border border-light-border-default dark:border-gray-600">
                          <HiLink className="text-light-text-secondary dark:text-gray-400 flex-shrink-0 text-sm" />
                          <span className="text-xs text-light-text-secondary dark:text-gray-300 truncate flex-1">
                            {`${window.location.host}/projects/${projectShareData.simulation}/sharelink`}
                          </span>
                          <button
                            onClick={() => copyShareableLink(
                              `${window.location.protocol}//${window.location.host}/projects/${projectShareData.simulation}/sharelink`,
                              "simulation"
                            )}
                            className="p-1 text-light-text-secondary dark:text-gray-300 hover:text-primary-light dark:hover:text-blue-400 hover:bg-light-interactive-hover dark:hover:bg-slate-600 rounded transition-colors duration-200 flex-shrink-0"
                          >
                            {copiedLinkType === "simulation" ? (
                              <HiCheck className="w-3 h-3 text-green-600 dark:text-green-400" />
                            ) : (
                              <HiClipboard className="w-3 h-3" />
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-xs text-light-text-secondary dark:text-gray-400 text-center py-2">
                      Generating shareable links...
                    </div>
                  )
                ) : (
                  <div className="text-xs text-light-text-secondary dark:text-gray-400 text-center py-2">
                    Enable shareable link to generate URLs
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="relative group">
          <ListGroup.Item
            className="hover:bg-light-interactive-hover dark:hover:bg-slate-700 border-none cursor-pointer transition-colors duration-200"
            theme={{
              link: {
                base: "flex justify-between items-center focus:outline-none w-full py-2 px-4 border-none text-light-text-primary dark:text-white",
              },
            }}
          >
            <span>Version Control</span> <HiOutlineChevronRight />
          </ListGroup.Item>
          <div className="absolute right-full top-0 w-44 bg-light-surfacePrimary dark:bg-slate-800 rounded-md shadow-lg border border-light-border-default dark:border-gray-700 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-opacity duration-75 z-50">
            <ListGroup className="bg-light-surfacePrimary dark:bg-slate-800 text-right border-none">
              <ListGroup.Item
                onClick={() => handleListGroupClick("merge")}
                className="hover:bg-light-interactive-hover dark:hover:bg-slate-700 border-none cursor-pointer transition-colors duration-200"
                theme={{
                  link: {
                    base: "flex justify-between items-center focus:outline-none w-full py-2 px-4 border-none text-light-text-primary dark:text-white",
                  },
                }}
              >
                <span>Merge</span> <FiGitMerge />
              </ListGroup.Item>
              <ListGroup.Item
                onClick={() => handleListGroupClick("reset")}
                className="hover:bg-light-interactive-hover dark:hover:bg-slate-700 border-none cursor-pointer transition-colors duration-200"
                theme={{
                  link: {
                    base: "flex justify-between items-center focus:outline-none w-full py-2 px-4 border-none text-light-text-primary dark:text-white",
                  },
                }}
              >
                <span>Reset</span> <HiRefresh />
              </ListGroup.Item>
              <ListGroup.Item
                onClick={() => handleListGroupClick("versionHistory")}
                className="hover:bg-light-interactive-hover dark:hover:bg-slate-700 border-none cursor-pointer transition-colors duration-200"
                theme={{
                  link: {
                    base: "flex justify-between items-center focus:outline-none w-full py-2 px-4 border-none text-light-text-primary dark:text-white",
                  },
                }}
              >
                <span>History</span> <BiHistory />
              </ListGroup.Item>
            </ListGroup>
          </div>
        </div>

        <ListGroup.Item
          onClick={() => handleListGroupClick("projectHistory")}
          className="hover:bg-light-interactive-hover dark:hover:bg-slate-700 border-none cursor-pointer transition-colors duration-200"
          theme={{
            link: {
              base: "flex justify-between items-center focus:outline-none w-full py-2 px-4 border-none text-light-text-primary dark:text-white",
            },
          }}
        >
          <span> History</span> <BiHistory />
        </ListGroup.Item>

        {org_role === "admin" && (
          <div className="relative group">
            <ListGroup.Item
              className="hover:bg-light-interactive-hover dark:hover:bg-slate-700 border-none cursor-pointer transition-colors duration-200"
              theme={{
                link: {
                  base: "flex justify-between items-center focus:outline-none w-full py-2 px-4 border-none text-light-text-primary dark:text-white",
                },
              }}
            >
              <span>Notifications</span> <HiOutlineChevronRight />
            </ListGroup.Item>
            <div className="absolute right-full top-0 w-64 bg-light-surfacePrimary dark:bg-slate-800 rounded-md shadow-lg border border-light-border-default dark:border-gray-700 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-opacity duration-75 z-50">
              <div className="p-4">
                <div className="flex justify-start">
                  <h4 className="text-sm font-semibold text-light-text-primary dark:text-white mb-4">
                    Notification Settings
                  </h4>
                </div>

                {notificationSettingLoading &&
                  (!nsd || Object.keys(nsd).length === 0) ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="h-6 w-6 border-2 border-light-text-secondary border-t-transparent rounded-full animate-spin"></div>
                  </div>
                ) : !nsd || Object.keys(nsd).length === 0 ? (
                  <div className="flex items-center justify-center py-4">
                    <div className="text-sm text-light-text-secondary dark:text-gray-400">
                      No notification settings available
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Notification Options */}
                    <div className="space-y-3">
                      {Object.keys(nsd || {}).map((settingKey) => {
                        const settingKeyMapping: any = {
                          project_summary: {
                            label: "Status Report",
                            icon: HiMail,
                          },
                          task_updates: { label: "Task Updates", icon: HiBell },
                        };
                        const setting = settingKeyMapping[settingKey];
                        const IconComponent = setting?.icon || HiCog;
                        const label = setting?.label || settingKey;

                        return (
                          <div
                            key={settingKey}
                            className="flex items-center justify-between p-2 rounded hover:bg-light-surfaceSecondary dark:hover:bg-slate-700 transition-colors duration-200"
                          >
                            <div className="flex items-center gap-2">
                              <IconComponent className="text-light-text-secondary dark:text-gray-400 w-4 h-4" />
                              <span className="text-sm text-light-text-primary dark:text-white">
                                {label}
                              </span>
                            </div>
                            {/* Custom Toggle Switch with individual loading */}
                            <div className="relative">
                              <button
                                type="button"
                                onClick={() => handleNotificationToggle(settingKey)}
                                disabled={notificationTogglesLoading[settingKey]}
                                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-gray-800 ${nsd[settingKey]
                                    ? "bg-blue-600 dark:bg-blue-500"
                                    : "bg-gray-200 dark:bg-gray-600"} ${notificationTogglesLoading[settingKey] ? "opacity-75 cursor-not-allowed" : "cursor-pointer"}`}
                              >
                                <span
                                  className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform duration-200 ease-in-out ${nsd[settingKey]
                                      ? "translate-x-5"
                                      : "translate-x-0.5"}`}
                                >
                                  {notificationTogglesLoading[settingKey] && (
                                    <div className="absolute inset-0 flex items-center justify-center">
                                      <div className="h-2 w-2 border border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                                    </div>
                                  )}
                                </span>
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Action Buttons */}
                    <div className="mt-4 pt-3 border-t border-light-border-default dark:border-gray-600">
                      <div className="flex gap-2">
                        <button
                          onClick={async () => {
                            if (currentEntity === ENTITY.PROJECT &&
                              currentEntityId &&
                              nsd) {
                              // Set loading for all toggles
                              const allKeys = Object.keys(nsd);
                              const loadingState = allKeys.reduce(
                                (acc, key) => {
                                  acc[key] = true;
                                  return acc;
                                },
                                {} as { [key: string]: boolean; }
                              );
                              setNotificationTogglesLoading(loadingState);

                              const enableAllPayload = allKeys.reduce(
                                (acc, key) => {
                                  acc[key] = true;
                                  return acc;
                                },
                                {} as any
                              );

                              try {
                                await updateProjectNotificationSetting(
                                  currentEntityId,
                                  enableAllPayload
                                );
                              } catch (error) {
                                toast.error("Failed to enable notifications");
                              } finally {
                                // Clear loading for all toggles
                                const clearedState = allKeys.reduce(
                                  (acc, key) => {
                                    acc[key] = false;
                                    return acc;
                                  },
                                  {} as { [key: string]: boolean; }
                                );
                                setNotificationTogglesLoading(clearedState);
                              }
                            }
                          } }
                          className="flex-1 py-1.5 px-2 bg-primary-light hover:bg-primary-dark text-white rounded text-xs font-medium transition-colors duration-200"
                        >
                          Enable All
                        </button>
                        <button
                          onClick={async () => {
                            if (currentEntity === ENTITY.PROJECT &&
                              currentEntityId &&
                              nsd) {
                              // Set loading for all toggles
                              const allKeys = Object.keys(nsd);
                              const loadingState = allKeys.reduce(
                                (acc, key) => {
                                  acc[key] = true;
                                  return acc;
                                },
                                {} as { [key: string]: boolean; }
                              );
                              setNotificationTogglesLoading(loadingState);

                              const disableAllPayload = allKeys.reduce(
                                (acc, key) => {
                                  acc[key] = false;
                                  return acc;
                                },
                                {} as any
                              );

                              try {
                                await updateProjectNotificationSetting(
                                  currentEntityId,
                                  disableAllPayload
                                );
                              } catch (error) {
                                toast.error("Failed to disable notifications");
                              } finally {
                                // Clear loading for all toggles
                                const clearedState = allKeys.reduce(
                                  (acc, key) => {
                                    acc[key] = false;
                                    return acc;
                                  },
                                  {} as { [key: string]: boolean; }
                                );
                                setNotificationTogglesLoading(clearedState);
                              }
                            }
                          } }
                          className="flex-1 py-1.5 px-2 bg-light-text-secondary hover:bg-light-text-primary text-white rounded text-xs font-medium transition-colors duration-200"
                        >
                          Disable All
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Resync button for Jira projects */}
        {(projectIntegrationData?.integration_type === "jira" ||
          projectIntegrationData?.integration_type === "azure") && (
            <ListGroup.Item
              onClick={() => {
                if (projectSyncStatus === "in_progress") return;
                handleListGroupClick("resyncProject");
              } }
              className={`border-none cursor-pointer transition-colors duration-200 ${projectSyncStatus === "in_progress"
                  ? "opacity-50 cursor-not-allowed bg-light-surfaceSecondary dark:bg-gray-700"
                  : "hover:bg-light-interactive-hover dark:hover:bg-slate-700"}`}
              style={{
                cursor: projectSyncStatus === "in_progress" ? "not-allowed" : "pointer",
              }}
              theme={{
                link: {
                  base: "flex justify-between items-center focus:outline-none w-full py-3 px-4 border-none text-light-text-primary dark:text-white",
                },
              }}
            >
              <div className="flex flex-col items-start space-y-1.5 flex-1">
                <div className="flex items-center justify-between w-full">
                  <span
                    className={`font-medium ${projectSyncStatus === "in_progress" ? "text-gray-500" : "text-light-text-primary dark:text-white"}`}
                  >
                    {projectSyncStatus === "in_progress"
                      ? "Syncing..."
                      : "Resync Epic"}
                  </span>
                  <HiRefresh
                    className={`w-5 h-5 ${projectSyncStatus === "in_progress"
                        ? "animate-spin text-gray-500"
                        : "text-gray-600 dark:text-gray-400"}`} />
                </div>
                <span className="text-xs text-gray-500 dark:text-gray-400 text-left leading-relaxed">
                  {projectLastSyncedAt
                    ? `Last: ${getTimeZoneDate(projectLastSyncedAt, "lll")}`
                    : "Last: Never"}
                </span>
              </div>
            </ListGroup.Item>
          )}
      </>;
    }
  ), projectIntegrationData);

  return (
    <div className="flex items-center justify-between w-full py-2 px-2 sm:px-4 bg-light-surfacePrimary dark:bg-secondary-dark border-b border-light-border-default dark:border-gray-700 min-h-[65px] max-h-[65px] gap-2">
      <div className="flex items-center min-w-0 flex-1  overflow-hidden truncate">
        <div className="min-w-0 " ref={breadcrumbContainerRef}>
          <Breadcrumb className="min-w-0 flex flex-nowrap items-center">
            <PreviousBreadCrumb />
            {breadcrumbs.map((item, index) => {
              const isItemLoading =
                isLoading &&
                index > 0 &&
                (!item.label || item.label === "Loading...");

              return (
                <Breadcrumb.Item
                  key={index}
                  className={`${item.active ? "text-light-text-secondary dark:text-gray-400" : " cursor-pointer"} min-w-0 flex items-center flex-shrink-0`}
                  onClick={
                    !item.active && onBreadcrumbClick
                      ? () => onBreadcrumbClick(index, item)
                      : undefined
                  }
                >
                  {isItemLoading ? (
                    <Skeleton
                      width={index === 1 ? "w-32" : "w-28"}
                      height="h-5"
                      className="inline-block align-middle"
                    />
                  ) : (
                    <TruncatedText
                      text={item.label}
                      className={`flex-1 ${
                        item.active
                          ? "text-light-text-secondary dark:text-gray-400 font-medium max-w-[200px]"
                          : "text-primary-light dark:text-blue-500 font-medium hover:underline cursor-pointer max-w-[200px]"
                      } 
                        inline-block truncate align-middle`}
                      placement="bottom"
                      alwaysShowTooltip={false}
                      onClick={
                        !item.active && onBreadcrumbClick
                          ? () => onBreadcrumbClick(index, item)
                          : undefined
                      }
                    />
                  )}
                </Breadcrumb.Item>
              );
            })}
          </Breadcrumb>
        </div>

        {/* Additional content beside breadcrumbs - not on Templates list (children go to right side there) */}
        {!isTemplatesListPage && (
          <div className="ml-2 flex-shrink-0">{children}</div>
        )}
      </div>

      <div className="flex items-center space-x-1 sm:space-x-2 flex-shrink-0 w-[40%] ml-auto justify-end">
        {/* On Templates list page, render children (Create template button) on the right */}
        {isTemplatesListPage && children && (
          <div className="flex-shrink-0">{children}</div>
        )}
        {/* Live/Simulation Toggle - Hidden on Templates list page (templates don't have Live/Sim mode) */}
        {!isTemplatesListPage && (
          <SimFlag
            variant="only_view_changes"
            simFlag={simFlag}
            changes={simulationChanges}
            readOnly={readOnly}
            isLoading={isLoading}
            onToggleSim={onToggleSim}
            onMerge={onMerge}
          />
        )}

        <div className="flex min-w-fit gap-2">
          {/* Create Task Button - Only show for projects */}
          {currentEntity === ENTITY.PROJECT && onCreateTask && (
            <div className="hidden sm:block">
              <CustomButton
                onClick={onCreateTask}
                className="flex items-center gap-1 bg-primary-light hover:bg-primary-dark text-light-text-inverse px-3 sm:px-4 py-1.5 sm:py-2 rounded text-sm transition-colors duration-200"
              >
                <HiPlusCircle className="text-base sm:text-lg" />
                <span className="hidden md:inline">Create Task</span>
                <span className="md:hidden">Task</span>
              </CustomButton>
            </div>
          )}

          {/* Refresh Button */}
          {showRefreshButton && onRefresh && (
            <div className="flex items-center gap-2 ">
              {currentEntity === ENTITY.ROADMAP && (
                <button
                  onClick={() => onLinkItems && onLinkItems()}
                  className={`flex items-center gap-1 text-light-text-inverse px-3 sm:px-4 py-1.5 sm:py-2 rounded-md text-sm focus:outline-none duration-200 shadow-md hover:shadow-lg transition-all ${"bg-primary-light dark:bg-primary-dark hover:bg-primary-hover dark:hover:bg-primary-light"}`}
                >
                  <HiPlusCircle className="text-base sm:text-lg" />
                  <span className="hidden lg:inline">Link Items</span>
                  <span className="lg:hidden">Link Items</span>
                </button>
              )}
              <button
                onClick={onRefresh}
                className={`flex items-center gap-1 text-light-text-inverse px-3 sm:px-4 py-1.5 sm:py-2 rounded-md text-sm focus:outline-none duration-200 shadow-md hover:shadow-lg transition-all bg-light-text-secondary dark:bg-slate-600 hover:bg-light-text-secondary/80 dark:hover:bg-slate-700`}
              >
                <HiRefresh className="text-base sm:text-lg" />
                <span className="hidden lg:inline">{refreshButtonText}</span>
                <span className="lg:hidden">Refresh</span>
              </button>
            </div>
          )}
        </div>

        {!isTemplatesListPage && (!readOnly || isTemplate) && (
          <div className="relative">
            <button
              onClick={() => setShowListGroup(!showListGroup)}
              className="p-1.5 sm:p-2 text-light-text-secondary rounded-full hover:bg-light-interactive-hover dark:hover:bg-slate-700 focus:outline-none flex-shrink-0 transition-colors duration-200"
            >
              <HiOutlineDotsVertical className="text-lg sm:text-xl" />
            </button>

            {showListGroup && (
              <>
                <div
                  className={`absolute right-0 top-full mt-1 z-50 ${isTemplate ? "w-56 sm:w-64" : "w-40 sm:w-44"} bg-light-surfacePrimary dark:bg-slate-800 rounded-md shadow-lg border border-light-border-default dark:border-gray-700`}
                >
                  <ListGroup className="bg-light-surfacePrimary dark:bg-slate-800 text-right border-none">
                    {isTemplate && renderTemplateMenuItems()}
                    {!isTemplate &&
                      currentEntity === ENTITY.ROADMAP &&
                      renderRoadmapMenuItems()}
                    {!isTemplate &&
                      currentEntity === ENTITY.PROJECT &&
                      renderProjectMenuItems()}

                    {/* Mobile-only buttons */}
                    <div className="sm:hidden border-t border-light-border-default dark:border-gray-600">
                      {currentEntity === ENTITY.PROJECT && onCreateTask && (
                        <ListGroup.Item
                          onClick={() => {
                            onCreateTask();
                            setShowListGroup(false);
                          }}
                          className="hover:bg-light-interactive-hover dark:hover:bg-slate-700 border-none cursor-pointer transition-colors duration-200"
                          theme={{
                            link: {
                              base: "flex justify-between items-center focus:outline-none w-full py-2 px-4 border-none text-light-text-primary dark:text-white",
                            },
                          }}
                        >
                          <span>Create Task</span> <HiPlusCircle />
                        </ListGroup.Item>
                      )}

                      {showRefreshButton && onRefresh && (
                        <ListGroup.Item
                          onClick={() => {
                            onRefresh();
                            setShowListGroup(false);
                          }}
                          className="hover:bg-light-interactive-hover dark:hover:bg-slate-700 border-none cursor-pointer transition-colors duration-200"
                          theme={{
                            link: {
                              base: "flex justify-between items-center focus:outline-none w-full py-2 px-4 border-none text-light-text-primary dark:text-white",
                            },
                          }}
                        >
                          <span>Refresh</span> <HiRefresh />
                        </ListGroup.Item>
                      )}
                    </div>
                  </ListGroup>
                </div>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowListGroup(false)}
                ></div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BreadcrumbHeader;
